import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API, fileUrl } from "@/lib/api";
import ShareButton from "@/components/ShareButton";
import { toast } from "sonner";
import {
  FileText, Sparkle, DownloadSimple, Eye, Plus, Lock, Globe, LinkSimple,
} from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function Resumes() {
  const [resumes, setResumes] = useState([]);
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/resumes").then((r) => setResumes(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      await api.post("/resumes", { title: "My Resume", template: "classic", privacy: "private" });
      toast.success("AI drafted your resume from your profile!");
      load();
    } catch { toast.error("Could not draft resume. Complete your profile first."); }
    finally { setCreating(false); }
  };

  const download = async (r) => {
    const token = localStorage.getItem("ga_token");
    window.open(`${API}/resumes/${r.id}/pdf?auth=${token}`, "_blank");
  };

  return (
    <main className="mx-auto max-w-4xl px-5 py-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline text-secondary mb-1">AI resume builder</p>
          <h1 className="font-heading text-3xl font-light tracking-tight sm:text-4xl">Your Resumes</h1>
        </div>
        <Button className="rounded-full gap-2" onClick={create} disabled={creating} data-testid="create-resume-btn">
          <Sparkle size={16} weight="fill" /> {creating ? "Drafting…" : "AI draft new resume"}
        </Button>
      </div>

      <Card className="mt-6 border-secondary/30 bg-secondary/5 p-6 shadow-sm">
        <div className="flex items-center gap-4">
          <span className="grid h-12 w-12 place-items-center rounded-xl bg-secondary text-secondary-foreground"><Sparkle size={24} weight="duotone" /></span>
          <p className="text-sm text-muted-foreground">Our AI drafts a polished resume from your profile, education timeline & achievements. Export to PDF and share via a branded SpLink + QR.</p>
        </div>
      </Card>

      <div className="mt-8 space-y-4">
        {resumes.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border py-16 text-center">
            <FileText size={40} className="mx-auto text-muted-foreground" weight="duotone" />
            <p className="mt-3 text-muted-foreground">No resumes yet. Draft your first with AI.</p>
          </div>
        )}
        {resumes.map((r) => (
          <Card key={r.id} className="flex flex-wrap items-center gap-4 border-border/60 p-5 shadow-sm" data-testid="resume-card">
            <span className="grid h-12 w-12 place-items-center rounded-xl bg-accent text-primary"><FileText size={24} weight="duotone" /></span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-heading text-lg font-medium">{r.title}</h3>
                <Badge variant="secondary" className="rounded-full font-normal capitalize gap-1">{r.privacy === "private" ? <Lock size={11} /> : <Globe size={11} />} {r.privacy}</Badge>
              </div>
              <p className="line-clamp-1 text-sm text-muted-foreground">{r.content?.summary || r.content?.headline}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link to={`/r/${r.public_slug}`} target="_blank"><Button size="sm" variant="outline" className="rounded-full gap-1.5"><Eye size={15} /> Preview</Button></Link>
              <Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={() => download(r)} data-testid="resume-download"><DownloadSimple size={15} /> PDF</Button>
              <ShareButton targetPath={`/r/${r.public_slug}`} entityType="resume" entityId={r.id} title={r.title} />
            </div>
          </Card>
        ))}
      </div>
    </main>
  );
}
